import type { Answers, QuestionId, QuizResult } from './types';
import { QUIZ_SCHEMA_VERSION } from './schema';

export const STORAGE_KEY = 'rm:quiz:v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface QuizPersistedState {
  schemaVersion: number;
  savedAt: string;
  currentStep: number;
  answers: Answers;
  result: QuizResult | null;
  startedAt: string | null;
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function saveQuizState(state: Omit<QuizPersistedState, 'schemaVersion' | 'savedAt'>): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const payload: QuizPersistedState = {
      schemaVersion: QUIZ_SCHEMA_VERSION,
      savedAt: new Date().toISOString(),
      ...state,
    };
    storage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // QuotaExceeded / private mode — silent.
  }
}

function isValidAnswers(v: unknown): v is Answers {
  if (typeof v !== 'object' || v === null) return false;
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof k !== 'string') return false;
    if (
      typeof val !== 'object' ||
      val === null ||
      !('questionId' in val) ||
      !('optionIds' in val) ||
      !Array.isArray((val as { optionIds: unknown }).optionIds)
    ) {
      return false;
    }
  }
  return true;
}

export function loadQuizState(): QuizPersistedState | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<QuizPersistedState>;
    if (parsed.schemaVersion !== QUIZ_SCHEMA_VERSION) return null;
    if (typeof parsed.savedAt !== 'string') return null;
    if (Date.now() - new Date(parsed.savedAt).getTime() > TTL_MS) return null;
    if (typeof parsed.currentStep !== 'number') return null;
    if (!isValidAnswers(parsed.answers)) return null;
    return {
      schemaVersion: parsed.schemaVersion,
      savedAt: parsed.savedAt,
      currentStep: parsed.currentStep,
      answers: parsed.answers as Partial<Record<QuestionId, Answers[QuestionId]>>,
      result: parsed.result ?? null,
      startedAt: typeof parsed.startedAt === 'string' ? parsed.startedAt : null,
    };
  } catch {
    return null;
  }
}

export function clearQuizState(): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
